import React, { useMemo, useState, useRef } from 'react';
import { Sheet, SheetContent, SheetTrigger, SheetClose } from '../ui/sheet';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { TrendingUp, Users, BarChart3, Euro, Calendar, PieChart, DollarSign, Package, TrendingDown, Award, FileText } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { formatEuro } from '../../utils/templates';
import { PageLayout, PageHeader, PageCard, pageStyles } from '../PageLayout';

const checkMatch = (allowed: any, c: any) => {
    const normalizeNumber = (v: any) => v ? String(v).replace(/[^0-9]/g, '').replace(/^0+/, '') : '';
    const normalizeName = (n: any) => n ? n.replace(/[:.;,]/g, ' ').replace(/\s+/g, ' ').trim() : '';

    if (!allowed || !Array.isArray(allowed)) return false;
    if (allowed.some(u => u.role === 'admin')) return true;
    return allowed.some(u => {
        if (!u) return false;
        const uAdv = normalizeNumber(u.adv), cAdp = normalizeNumber(c.adp), cBtnr = normalizeNumber(c.btnr), uBtnr = normalizeNumber(u.btnr);
        if (uAdv.length > 2 && ((cAdp && uAdv.includes(cAdp)) || (cBtnr && uAdv.includes(cBtnr)))) return true;
        if (uBtnr.length > 2 && ((cAdp && uBtnr.includes(cAdp)) || (cBtnr && uBtnr.includes(cBtnr)))) return true;
        const uName = normalizeName(u.name).toLowerCase(), cAdpName = normalizeName(c.adp).toLowerCase();
        return uName.length > 2 && cAdpName.includes(uName);
    });
};

const BestandsView: React.FC<{ customers: any[]; currentUser: any; allUsers: any[]; onNavigateTo: (view: string) => void }> = ({ customers, currentUser, allUsers, onNavigateTo }) => {
    const { theme } = useTheme();
    const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
    // Filter-States
    const [selectedSparte, setSelectedSparte] = useState<string | null>('');
    const [minContracts, setMinContracts] = useState<string>('');
    const [maxContracts, setMaxContracts] = useState<string>('');
    const [showSheet, setShowSheet] = useState(false);
    const [detailsTab, setDetailsTab] = useState<'filter'|'kundenliste'>('filter');
    const [filterTab, setFilterTab] = useState<'vertraege'|'sparte'|'gesellschaft'|'kunde'>('vertraege');
    const tableRef = useRef<HTMLDivElement>(null);





    // myCustomers muss vor der Verwendung initialisiert werden
    const myCustomers = useMemo(() => {
        if (!currentUser) return [];
        if (currentUser.role === 'admin') return customers;
        const allowedUserIds = [currentUser.id, ...(currentUser.allowedToView || [])];
        return customers.filter((c: any) => checkMatch(allUsers.filter((u: any) => allowedUserIds.includes(u.id)), c));
    }, [customers, currentUser, allUsers]);


    const allSparten = useMemo(() => {
        const spartenSet = new Set<string>();
        myCustomers.forEach((c) => {
            (c.verträge || []).forEach((v: any) => {
                if (v.sparte) spartenSet.add(v.sparte);
            });
        });
        return Array.from(spartenSet).sort();
    }, [myCustomers]);

    const parseMoney = (val: any): number => {
        if (val == null) return 0;
        let str = String(val).replace(/[€$EUR\s]/gi, '').trim();
        if (!str) return 0;
        if (str.includes('.') && str.includes(',')) {
            if (str.lastIndexOf(',') > str.lastIndexOf('.')) {
                str = str.replace(/\./g, '').replace(',', '.');
            } else {
                str = str.replace(/,/g, '');
            }
        } else if (str.includes(',')) {
            str = str.replace(',', '.');
        }
        const num = parseFloat(str);
        return isNaN(num) ? 0 : num;
    };



    const stats = useMemo(() => {
        let totalBeitrag = 0;
        let totalMOB = 0;
        let contractsBySparte: Record<string, number> = {};
        let beitragBySparte: Record<string, number> = {};
        let mobBySparte: Record<string, number> = {};
        let zahlweiseCount: Record<string, number> = { monatlich: 0, vierteljährlich: 0, halbjährlich: 0, jährlich: 0 };
        let zahlweiseBeitrag: Record<string, number> = { monatlich: 0, vierteljährlich: 0, halbjährlich: 0, jährlich: 0 };
        let contractCount = 0;
        let gesellschaftCount: Record<string, number> = {};

        myCustomers.forEach((c) => {
            (c.verträge || []).forEach((v: any) => {
                contractCount++;
                const beitrag = parseMoney(v.beitrag);
                const mob = parseMoney(v.mob);
                totalBeitrag += beitrag;
                totalMOB += mob;

                const sparte = v.sparte || 'Unbekannt';
                contractsBySparte[sparte] = (contractsBySparte[sparte] || 0) + 1;
                beitragBySparte[sparte] = (beitragBySparte[sparte] || 0) + beitrag;
                mobBySparte[sparte] = (mobBySparte[sparte] || 0) + mob;

                const gesellschaft = v.gesellschaft || 'Unbekannt';
                gesellschaftCount[gesellschaft] = (gesellschaftCount[gesellschaft] || 0) + 1;

                const zw = String(v.zahlweise || '').toLowerCase();
                if (zw.includes('monat')) {
                    zahlweiseCount.monatlich++;
                    zahlweiseBeitrag.monatlich += beitrag;
                } else if (zw.includes('vierteljähr') || zw.includes('quartal')) {
                    zahlweiseCount.vierteljährlich++;
                    zahlweiseBeitrag.vierteljährlich += beitrag;
                } else if (zw.includes('halbjähr') || zw.includes('halb')) {
                    zahlweiseCount.halbjährlich++;
                    zahlweiseBeitrag.halbjährlich += beitrag;
                } else if (zw.includes('jähr') || zw.includes('jahr')) {
                    zahlweiseCount.jährlich++;
                    zahlweiseBeitrag.jährlich += beitrag;
                }
            });
        });

        const sortedSparten = Object.entries(beitragBySparte)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 15);

        const sortedGesellschaften = Object.entries(gesellschaftCount)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 10);

        return {
            totalBeitrag,
            avgMOB: contractCount ? totalMOB / contractCount : 0,
            contractCount,
            customerCount: myCustomers.length,
            crossSellingQuote: myCustomers.length ? contractCount / myCustomers.length : 0,
            avgContractsPerCustomer: myCustomers.length ? (contractCount / myCustomers.length).toFixed(2) : '0',
            avgBeitragPerCustomer: myCustomers.length ? totalBeitrag / myCustomers.length : 0,
            avgBeitragPerContract: contractCount ? totalBeitrag / contractCount : 0,
            contractsBySparte,
            beitragBySparte,
            mobBySparte,
            sortedSparten,
            zahlweiseCount,
            zahlweiseBeitrag,
            sortedGesellschaften,
            gesellschaftCount,
        };
    }, [myCustomers]);

    const spartenDetails = selectedSparte ? {
        contracts: stats.contractsBySparte[selectedSparte] || 0,
        beitrag: stats.beitragBySparte[selectedSparte] || 0,
        mob: stats.mobBySparte[selectedSparte] || 0,
    } : null;

    return (
        <PageLayout>
            <PageHeader
                title="Bestandsauswertung"
                subtitle={`Detaillierte Übersicht über ${currentUser?.role === 'admin' ? 'den gesamten' : 'Ihren'} Versicherungsbestand`}
                actions={
                    <button
                        className={`flex items-center gap-2 px-4 py-2 rounded-xl font-semibold text-sm shadow border transition-colors ${isDark ? 'bg-blue-600 text-white border-blue-400 hover:bg-blue-700' : 'bg-blue-100 text-blue-900 border-blue-300 hover:bg-blue-200'}`}
                        onClick={() => onNavigateTo('bestand-filter')}
                    >
                        <BarChart3 size={18} /> Erweiterte Filter
                    </button>
                }
            />

            {/* Hauptkennzahlen */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Gesamtbeitrag */}
                <div className={`p-6 rounded-2xl border ${isDark ? 'bg-gradient-to-br from-cyan-500/20 to-cyan-500/10 border-cyan-400/30' : 'bg-gradient-to-br from-cyan-50 to-cyan-100 border-cyan-200'}`}> 
                    <div className="flex items-center gap-3 mb-2">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center border ${isDark ? 'bg-cyan-500/30 border-cyan-400/50 text-cyan-300' : 'bg-cyan-200 border-cyan-300 text-cyan-700'}`}> 
                            <Euro size={24} />
                        </div>
                        <div>
                            <p className={`text-xs font-bold uppercase ${isDark ? 'text-cyan-300' : 'text-cyan-700'}`}>Gesamtbeitrag (Jahr)</p>
                            <p className={`text-2xl font-bold ${isDark ? 'text-cyan-200' : 'text-cyan-900'}`}>{formatEuro(stats.totalBeitrag)}</p>
                        </div>
                    </div>
                </div>
                
                {/* Ø MOB (durchschnittlicher MOB je Vertrag) */}
                <div className={`p-6 rounded-2xl border ${isDark ? 'bg-gradient-to-br from-green-500/20 to-green-500/10 border-green-400/30' : 'bg-gradient-to-br from-green-50 to-green-100 border-green-200'}`}> 
                    <div className="flex items-center gap-3 mb-2">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center border ${isDark ? 'bg-green-500/30 border-green-400/50 text-green-300' : 'bg-green-200 border-green-300 text-green-700'}`}> 
                            <TrendingUp size={24} />
                        </div>
                        <div>
                            <p className={`text-xs font-bold uppercase ${isDark ? 'text-green-300' : 'text-green-700'}`}>Ø MOB pro Vertrag</p>
                            <p className={`text-2xl font-bold ${isDark ? 'text-green-200' : 'text-green-900'}`}>{formatEuro(stats.avgMOB)}</p>
                        </div>
                    </div>
                </div>
                
                {/* Kunden */}
                <div className={`p-6 rounded-2xl border ${isDark ? 'bg-gradient-to-br from-blue-500/20 to-blue-500/10 border-blue-400/30' : 'bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200'}`}> 
                    <div className="flex items-center gap-3 mb-2">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center border ${isDark ? 'bg-blue-500/30 border-blue-400/50 text-blue-300' : 'bg-blue-200 border-blue-300 text-blue-700'}`}> 
                            <Users size={24} />
                        </div>
                        <div>
                            <p className={`text-xs font-bold uppercase ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>Kunden</p>
                            <p className={`text-2xl font-bold ${isDark ? 'text-blue-200' : 'text-blue-900'}`}>{stats.customerCount}</p>
                        </div>
                    </div>
                </div>
                
                {/* Verträge */}
                <div className={`p-6 rounded-2xl border ${isDark ? 'bg-gradient-to-br from-purple-500/20 to-purple-500/10 border-purple-400/30' : 'bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200'}`}> 
                    <div className="flex items-center gap-3 mb-2">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center border ${isDark ? 'bg-purple-500/30 border-purple-400/50 text-purple-300' : 'bg-purple-200 border-purple-300 text-purple-700'}`}> 
                            <FileText size={24} />
                        </div>
                        <div>
                            <p className={`text-xs font-bold uppercase ${isDark ? 'text-purple-300' : 'text-purple-700'}`}>Verträge</p>
                            <p className={`text-2xl font-bold ${isDark ? 'text-purple-200' : 'text-purple-900'}`}>{stats.contractCount}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Top Sparten als Accordion untereinander */}
            <div className="space-y-6">
                {/* Nach Beitrag (Accordion) */}
                <div className={pageStyles.card(isDark)}>
                    <details>
                        <summary className="cursor-pointer select-none outline-none focus:ring-2 focus:ring-blue-400 rounded-2xl p-6">
                            <h3 className={`text-lg font-bold flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                                <BarChart3 size={20} className={isDark ? 'text-rose-400' : 'text-rose-600'} />
                                Top Sparten nach Beitrag
                            </h3>
                        </summary>
                        <div className="p-6 pt-0 space-y-3">
                            {stats.sortedSparten.map(([sparte, beitrag], idx) => (
                                <div key={sparte} className="flex items-center gap-3 cursor-pointer group" onClick={() => setSelectedSparte(selectedSparte === sparte ? null : sparte)}>
                                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center font-bold text-xs border flex-shrink-0 ${isDark ? 'bg-gradient-to-br from-rose-500/20 to-pink-500/20 text-white/80 border-rose-400/30' : 'bg-gradient-to-br from-rose-100 to-pink-100 text-rose-700 border-rose-300'}`}>
                                        {idx + 1}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex justify-between items-center mb-1">
                                            <span className={`font-medium truncate ${isDark ? 'text-white' : 'text-slate-900'}`}>{sparte}</span>
                                            <span className={`font-bold text-sm ml-2 ${isDark ? 'text-white/80' : 'text-slate-700'}`}>{formatEuro(beitrag)}</span>
                                        </div>
                                        <div className={`w-full rounded-full h-1.5 overflow-hidden ${isDark ? 'bg-white/10' : 'bg-slate-200'}`}>
                                            <div className={`h-full rounded-full transition-all ${isDark ? 'bg-gradient-to-r from-rose-500/60 to-pink-500/60' : 'bg-gradient-to-r from-rose-400 to-pink-400'}`} style={{ width: `${stats.totalBeitrag ? (beitrag / stats.totalBeitrag) * 100 : 0}%` }} />
                                        </div>
                                        <div className={`flex justify-between text-[10px] mt-1 ${isDark ? 'text-white/50' : 'text-slate-500'}`}>
                                            <span>{stats.contractsBySparte[sparte]} Verträge</span>
                                            <span>{stats.totalBeitrag ? ((beitrag / stats.totalBeitrag) * 100).toFixed(1) : 0}%</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </details>
                </div>

                {/* Nach Verträgen (Accordion) */}
                <div className={`border rounded-2xl ${isDark ? 'glass-card border-white/20' : 'bg-white border-slate-200 shadow-sm'}`}>
                    <details>
                        <summary className="cursor-pointer select-none outline-none focus:ring-2 focus:ring-blue-400 rounded-2xl p-6">
                            <h3 className={`text-lg font-bold flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                                <BarChart3 size={20} className={isDark ? 'text-indigo-400' : 'text-indigo-600'} />
                                Top Sparten nach Verträgen
                            </h3>
                        </summary>
                        <div className="p-6 pt-0 space-y-3">
                            {Object.entries(stats.contractsBySparte)
                                .sort((a, b) => (b[1] as number) - (a[1] as number))
                                .slice(0, 15)
                                .map(([sparte, count], idx) => (
                                    <div key={sparte} className="flex items-center gap-3">
                                        <div className={`w-7 h-7 rounded-lg flex items-center justify-center font-bold text-xs border flex-shrink-0 ${isDark ? 'bg-gradient-to-br from-indigo-500/20 to-purple-500/20 text-white/80 border-indigo-400/30' : 'bg-gradient-to-br from-indigo-100 to-purple-100 text-indigo-700 border-indigo-300'}`}>
                                            {idx + 1}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex justify-between items-center mb-1">
                                                <span className={`font-medium truncate ${isDark ? 'text-white' : 'text-slate-900'}`}>{sparte}</span>
                                                <span className={`font-bold text-sm ml-2 ${isDark ? 'text-white/80' : 'text-slate-700'}`}>{count}</span>
                                            </div>
                                            <div className={`w-full rounded-full h-1.5 overflow-hidden ${isDark ? 'bg-white/10' : 'bg-slate-200'}`}>
                                                <div className={`h-full rounded-full transition-all ${isDark ? 'bg-gradient-to-r from-indigo-500/60 to-purple-500/60' : 'bg-gradient-to-r from-indigo-400 to-purple-400'}`} style={{ width: `${stats.contractCount ? (count / stats.contractCount) * 100 : 0}%` }} />
                                            </div>
                                            <div className={`flex justify-between text-[10px] mt-1 ${isDark ? 'text-white/50' : 'text-slate-500'}`}>
                                                <span>{formatEuro(stats.beitragBySparte[sparte] || 0)}</span>
                                                <span>{stats.contractCount ? ((count / stats.contractCount) * 100).toFixed(1) : 0}%</span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                        </div>
                    </details>
                </div>
            </div>

            {/* Sparten-Details Modal */}
            {selectedSparte && spartenDetails && (
                <div className={`fixed bottom-4 right-4 z-50 rounded-2xl p-6 shadow-2xl max-w-sm animate-slide-up border ${isDark ? 'glass-card bg-gradient-to-br from-blue-500/20 to-indigo-500/20 border-blue-400/30' : 'bg-white/95 border-slate-200'}`}>
                    <h4 className={`font-bold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>{selectedSparte}</h4>
                    <div className="space-y-2">
                        <div className="flex justify-between">
                            <span className={`text-sm ${isDark ? 'text-white/70' : 'text-slate-600'}`}>Verträge:</span>
                            <span className={`font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{spartenDetails.contracts}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className={`text-sm ${isDark ? 'text-white/70' : 'text-slate-600'}`}>Beitrag:</span>
                            <span className={`font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{formatEuro(spartenDetails.beitrag)}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className={`text-sm ${isDark ? 'text-white/70' : 'text-slate-600'}`}>MOB:</span>
                            <span className={`font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{formatEuro(spartenDetails.mob)}</span>
                        </div>
                    </div>
                    <button onClick={() => setSelectedSparte(null)} className={`mt-4 w-full py-2 rounded-lg font-semibold text-sm transition-colors ${isDark ? 'bg-white/10 hover:bg-white/20 text-white' : 'bg-slate-100 hover:bg-slate-200 text-slate-900'}`}>
                        Schließen
                    </button>
                </div>
            )}


            {/* ...kein Modal, keine Buttons für Filterseite... */}

        </PageLayout>
    );
};

export default BestandsView;
